/**
 * Buscador del menú lateral — filtrado puro sobre el modelo de navegación.
 *
 * Vive separado del renderer por la misma razón que navModel.ts: el menú tiene
 * 13 grupos y ~45 destinos, así que la regla de qué matchea y qué no es lo
 * único que hay que poder testear sin montar el sidebar entero.
 *
 * Las funciones reciben los grupos YA filtrados por rol y feature flags: el
 * buscador no puede ser una puerta trasera a ítems que el usuario no ve.
 */
import { NavGroup, NavItem } from "./navModel";

/** Grupo con sus ítems ya filtrados por permisos y flags, como lo arma el renderer. */
export interface VisibleNavGroup {
  group: NavGroup;
  items: NavItem[];
}

export interface NavSearchGroup extends VisibleNavGroup {
  /** El texto buscado matchea el label del grupo: se muestran todos sus ítems. */
  groupMatched: boolean;
}

/** Destino navegable dentro de los resultados, en el orden en que se ven. */
export interface NavSearchTarget {
  to: string;
  label: string;
}

/** Tramo de un label, partido para resaltar lo que matcheó. */
export interface NavTextSegment {
  text: string;
  match: boolean;
}

const stripDiacritics = (value: string): string =>
  value.normalize("NFD").replace(/[̀-ͯ]/g, "");

/** "Analítica" y "analitica" tienen que ser la misma búsqueda. */
export const normalizeNavText = (value: string): string => stripDiacritics(value).toLowerCase();

/**
 * "lista event" → ["lista", "event"]. Se exigen TODOS los tokens, no la frase
 * literal: así el orden en que se escribe no importa ("evento lista" también
 * encuentra "Lista de eventos").
 */
export const toSearchTokens = (query: string): string[] =>
  normalizeNavText(query).split(/\s+/).filter(Boolean);

const matchesTokens = (haystack: string, tokens: string[]): boolean => {
  const normalized = normalizeNavText(haystack);
  return tokens.every((token) => normalized.includes(token));
};

/**
 * Filtra el modelo por texto libre.
 *
 * - Si matchea el label del grupo, el grupo se muestra entero: buscar "eventos"
 *   tiene que listar todo el módulo, no solo los ítems que repiten la palabra.
 * - Si no, el grupo sobrevive solo con los ítems que matchean.
 *
 * Sin tokens devuelve el modelo intacto, así el renderer no necesita ramificar.
 */
export const filterNavGroups = (
  groups: VisibleNavGroup[],
  tokens: string[]
): NavSearchGroup[] => {
  if (tokens.length === 0) {
    return groups.map((visible) => ({ ...visible, groupMatched: false }));
  }

  return groups.reduce<NavSearchGroup[]>((acc, visible) => {
    const { group, items } = visible;

    if (matchesTokens(group.label, tokens)) {
      // Un grupo que matchea por nombre pero se quedó sin ítems visibles (todos
      // detrás de un flag apagado) no es un resultado: sería un encabezado
      // suelto, sin nada a donde ir.
      if (group.to || items.length > 0) acc.push({ group, items, groupMatched: true });
      return acc;
    }

    // El label del grupo entra en el texto del ítem: "eventos agregar"
    // encuentra "Eventos › Agregar evento" aunque ninguna palabra sola alcance.
    const matched = items.filter((item) => matchesTokens(`${group.label} ${item.label}`, tokens));
    if (matched.length > 0) acc.push({ group, items: matched, groupMatched: false });

    return acc;
  }, []);
};

/**
 * Resultados aplanados en orden de lectura, para moverse con las flechas y
 * navegar con Enter. Un grupo que es enlace directo (sin acordeón) aporta su
 * propio destino; el resto aporta sus ítems.
 */
export const flattenNavTargets = (results: NavSearchGroup[]): NavSearchTarget[] =>
  results.flatMap(({ group, items }) =>
    group.to
      ? [{ to: group.to, label: group.label }]
      : items.map((item) => ({ to: item.to, label: item.label }))
  );

/**
 * Parte un label en tramos que matchean y tramos que no, para resaltarlos.
 *
 * Se normaliza carácter por carácter y no de una para que los índices del texto
 * normalizado sigan valiendo sobre el original: si se normalizara entero, sacar
 * las tildes correría las posiciones y el resaltado terminaría un carácter
 * desplazado en "Analítica" o "Configuración".
 */
export const highlightSegments = (label: string, tokens: string[]): NavTextSegment[] => {
  if (tokens.length === 0) return [{ text: label, match: false }];

  const chars = Array.from(label);
  const normalized = chars.map((char) => normalizeNavText(char)).join("");
  // Un carácter que no normaliza 1 a 1 rompería el mapeo de índices. Antes que
  // resaltar el tramo equivocado, se muestra el label sin resaltar.
  if (normalized.length !== chars.length) return [{ text: label, match: false }];

  const hit = new Array<boolean>(chars.length).fill(false);
  tokens.forEach((token) => {
    let from = normalized.indexOf(token);
    while (from !== -1) {
      for (let i = from; i < from + token.length; i++) hit[i] = true;
      from = normalized.indexOf(token, from + 1);
    }
  });

  return chars.reduce<NavTextSegment[]>((segments, char, index) => {
    const last = segments[segments.length - 1];
    if (last && last.match === hit[index]) last.text += char;
    else segments.push({ text: char, match: hit[index] });
    return segments;
  }, []);
};
